import { Invoice } from '../../../types';

export function ModernInvoicePreview({ invoice, company }: { invoice: Invoice, company: any, workspace: any }) {
  const brandColor = company.primaryColor || '#2563EB';
  const gradient = `linear-gradient(90deg, ${brandColor} 60%, #60a5fa 100%)`;
  return (
    <div className="bg-white mx-auto shadow-lg rounded-lg overflow-hidden print:shadow-none" style={{ width: '210mm', minHeight: '297mm', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header Bar */}
      <div className="w-full" style={{ background: gradient, height: 70 }}>
        <div className="flex items-center h-full px-12">
          <div className="flex-1 flex items-center">
            {company.logo && <img src={company.logo} alt={company.companyName} className="h-14 w-auto mr-6 object-contain bg-white rounded shadow" />}
            <span className="text-white text-3xl font-extrabold tracking-tight drop-shadow">{company.companyName}</span>
          </div>
          <div className="text-white text-right">
            <div className="text-xl font-bold tracking-wider">INVOICE</div>
            <div className="text-lg font-semibold">{invoice.invoiceNumber}</div>
          </div>
        </div>
      </div>
      {/* Company Info & Invoice Details */}
      <div className="flex justify-between px-12 pt-10">
        <div className="text-base text-gray-700 space-y-1">
          {company.licenseNumber && (<div>License: {company.licenseNumber}</div>)}
          <div style={{ whiteSpace: 'pre-line' }}>{company.address}</div>
          <div>Phone: {company.phone}</div>
          {company.email && <div>{company.email}</div>}
        </div>
        <div className="text-base text-right text-gray-700 space-y-1">
          <div>Date: <span className="font-semibold">{new Date(invoice.issueDate).toLocaleDateString('en-GB')}</span></div>
          <div>Due: <span className="font-semibold">{new Date(invoice.dueDate).toLocaleDateString('en-GB')}</span></div>
        </div>
      </div>
      {/* Bill To */}
      <div className="px-12 pt-10">
        <div className="text-lg font-bold text-gray-800 mb-1">Bill To</div>
        <div className="text-xl font-bold text-gray-900">{invoice.client.name}</div>
        <div className="text-base text-gray-700">{invoice.client.address}</div>
        {invoice.client.phone && <div className="text-base text-gray-700">Phone: {invoice.client.phone}</div>}
        <div className="text-base text-gray-700">{invoice.client.email}</div>
      </div>
      {/* Items Table */}
      <div className="px-12 pt-10">
        <div className="grid grid-cols-12 gap-4 p-3 text-base font-bold text-white rounded-t-xl" style={{ background: gradient }}>
          <div className="col-span-6">DESCRIPTION</div>
          <div className="col-span-2 text-center">RATE</div>
          <div className="col-span-2 text-center">QTY</div>
          <div className="col-span-2 text-right">AMOUNT</div>
        </div>
        <div className="border-x border-b border-gray-200 rounded-b-xl overflow-hidden">
          {invoice.items.map((item, index) => (
            <div key={item.id} className={`grid grid-cols-12 gap-4 p-3 text-base ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`}>
              <div className="col-span-6">{item.description}</div>
              <div className="col-span-2 text-center">{item.rate}</div>
              <div className="col-span-2 text-center">{item.quantity}</div>
              <div className="col-span-2 text-right">{item.amount}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Totals */}
      <div className="px-12 pt-10 flex justify-end">
        <div className="w-96 bg-white rounded-xl shadow p-6 border border-blue-100">
          <div className="flex justify-between text-base mb-2"><span>Subtotal</span><span>{invoice.subtotal}</span></div>
          <div className="flex justify-between text-base mb-2"><span>Tax ({invoice.taxRate}%)</span><span>{invoice.taxAmount}</span></div>
          {invoice.discountAmount > 0 && <div className="flex justify-between text-base text-green-600 mb-2"><span>Discount</span><span>-{invoice.discountAmount}</span></div>}
          <div className="flex justify-between text-xl font-extrabold border-t pt-4 mt-4" style={{ color: brandColor }}><span>Total</span><span>{invoice.total}</span></div>
        </div>
      </div>
      {/* Thank You */}
      <div className="text-center text-gray-500 text-lg mt-16 font-semibold tracking-wide">Thank you for your business!</div>
      {/* Signature */}
      {invoice.signature && (
        <div className="mt-12 text-center border-t pt-8 border-gray-200">
          <div className="text-xs text-gray-500 mb-1">Authorized Signature</div>
          {invoice.signature.startsWith('http') ? (
            <img src={invoice.signature} alt="Signature" className="mx-auto h-14 object-contain" />
          ) : (
            <div className="italic text-xl font-signature text-gray-700" style={{ fontFamily: 'cursive, serif' }}>{invoice.signature}</div>
          )}
        </div>
      )}
    </div>
  );
} 